import React, {
    createContext, useContext, useEffect, useState, useRef,
} from 'react';
import { useDelayedLoading } from './hooks';
import { io } from 'socket.io-client';
import { ProFormaModelIds } from 'constants/proForma';
import {
    IProFormaSocketRoomData,
    IProFormaGeneralInfo,
    IUpdateProFormaKeyValues,
    ICreateBudgetClassificationPostBody,
    ICreateProFormaSourceRequestData,
    IBudgetClassificationPatchData,
    IGrossBuildableAreaLotsData,
    IGrossBuildableAreaLot,
    IUsesSummary,
    ISourcesSummary,
    ISourcePatchData,
    IOperatingAndPermanentDebtInfo,
    IIncomeSummary,
    IExpenseSummary,
    ILeasingCostReserves,
    IProFormaPageLocalStorage,
    IPatchOperatingInfoData,
    IOperatingPeriodCashFlows,
    IDispositionData,
    IProFormaReturnsData,
    IWaterfallAnalysis,
    IAssumptions,
    IUpdateDistributionWaterfallData,
    IWaterfallType,
    IProFormaPartner,
    ISourceType,
    DebtMetricType,
    IImportBudgetClassificationFromProjectData,
    IEditor,
    IRecoverableExpenseUpdate,
} from 'views/ProFormaTable/types';
import { PageSpecificKeys, useLocalStorage, GlobalKeys } from 'helpers/localStorage';
import {
    PRO_FORMA_WS_EVENT_NAMES, DEFUALT_ROW_NAME_BY_HANDLER, SHOW_CENTS_KEY,
    ProFormaAreaMode, WATTS_TYPE, WattsType,
    SELECTED_SCENARIO_KEY,
    FORECAST_METRIC_OPTIONS_NON_EARLY_PLANNING,
    SELECTED_PERIOD_CADENCE_KEY,
} from 'views/ProFormaTable/constants';
import useToast from 'hooks/useToast';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import { useGetProFormaRoom } from 'queries/ProForma';
import { IOptionAnyValue } from 'bricks/types';
import { isEmpty } from 'lodash';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { useAuth0TokenContext } from '../../../../../contexts/Auth0TokenContext';
import { config } from '../../../../../config';
import { formatProFormaValue } from '../helpers';

const joinRoom = (socket: any, roomId: string) => new Promise((resolve, reject) => {
    socket.emit('join_room', roomId, (response: any) => {
        if (response && response.error) {
            console.error('Error joining room:', response.error);
            reject(response.error);
        } else {
            console.log('Successfully joined room:', roomId);
            resolve(response);
        }
    });
});

const leaveRoom = (socket: any, roomId: string) => new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
        reject(new Error('leave_room timeout'));
    }, 2000); // 2 second timeout

    socket.emit('leave_room', roomId, () => {
        clearTimeout(timeout);
        console.log('Left room:', roomId);
        resolve();
    });
});

const getProForma = (socket: any, roomId: string) => new Promise((resolve, reject) => {
    socket.emit('get_pro_forma', { room: roomId });

    socket.once('pro_forma:fetched', (data: IProFormaSocketRoomData) => {
        console.log('Pro forma data:', data);
        resolve(data);
    });
}) as Promise<IProFormaSocketRoomData>;

const DEFAULT_BY_CADENCE: Record<Periods, any[]> = {
    [Periods.MONTHLY]: [],
    [Periods.QUARTERLY]: [],
    [Periods.ANNUALLY]: [],
};

const DEFAULT_EMPTY_LOT: IGrossBuildableAreaLotsData = {
    grossBuildableAreaLots: [],
    totalGrossBuildableArea: 0,
    totalLandAreaSquareFootage: 0,
    totalFar: 0,
};

const DEFAULT_WATTS_TYPE: WattsType = WattsType.KILOWATTS;

const defaultValues: IProFormaSocketContext = {
    proForma: {
        id: 0,
        proFormaStatusId: 0,
        proFormaModelId: ProFormaModelIds.APARTMENT,
        proFormaModelName: '',
        dealName: '',
        dealId: 0,
        boeScenarioId: 0,
        name: '',
        description: '',
        address: '',
        city: '',
        county: '',
        state: '',
        zipCode: '',
        associatedUniversity: null,
        startDate: '',
        saleDate: '',
        saleMonth: null,
        landArea: null,
        landAreaUnitTypeId: 6,
        floorAreaRatio: null,
        grossBuildableArea: null,
        buildings: 1,
        averageStories: 1,
        averageClearHeight: null,
        isFloorPlateAreaVaried: false,
        storyArea: [],
        builtGrossSquareFootage: null,
        structuredParkingSpaces: null,
        surfaceParkingSpaces: null,
        undergroundParkingSpaces: null,
        trailerParkingSpaces: null,
        isActive: false,
        createdAt: '',
        updatedAt: '',
        subprojectId: null,
        areaMode: ProFormaAreaMode.FAR,
        calculateArea: false,
        targetLeveredIrr: null,
        holdPeriod: null,
        constructionStart: '',
        constructionDuration: 24,
    },
    handleUpdateProForma: () => { },
    handleCreateDefaultBudgetClassifications: () => { },
    isDefaultBudgetClassificationsLoading: false,
    handleCreateRecommendedCategories: () => { },
    isRecommendedCategoriesLoading: false,
    handleCreateBudgetClassification: () => { },
    isBudgetClassificationLoading: false,
    handleUpdateBudgetClassifications: () => { },
    isBudgetClassificationUpdatingLoading: false,
    handleDeleteBudgetClassification: () => { },
    isBudgetClassificationDeletionLoading: false,
    handleImportBudgetClassificationFromProject: () => { },
    isImportFromProjectBudgetClassificationLoading: false,
    handleCopyBudgetCategorizations: () => { },
    handledDeleteSource: () => { },
    isSourceDeletionLoading: false,
    handleCreateProFormaSource: () => { },
    buildableLots: DEFAULT_EMPTY_LOT,
    uses: {} as IUsesSummary,
    sources: {} as ISourcesSummary,
    handleCreateBuildableLot: () => { },
    handleUpdateBuildableLot: () => { },
    handleDeleteBuildableLot: () => { },
    handleUpdateSource: () => { },
    handleUpdateSourcesMetricType: () => { },
    isUpdatingSourcesMetricType: false,
    operatingInfo: {} as IOperatingAndPermanentDebtInfo,
    operatingPeriodCashFlows: [] as IOperatingPeriodCashFlows[],
    incomeSummary: {} as IIncomeSummary,
    expenses: {} as IExpenseSummary,
    leasingCostReserves: undefined,
    shouldUseProjectActuals: false,
    setShouldUseProjectActuals: () => { },
    handleUpdateProFormaOperations: () => { },
    handleCreateOperationsRow: () => { },
    handleUpdateOperationsRow: () => (_e: React.ChangeEvent<HTMLInputElement>) => { },
    handleDeleteOperationsRow: () => { },
    socketRef: { current: null } as React.MutableRefObject<any>,
    disposition: undefined,
    handleUpdateProFormaAssumptions: () => { },
    returnsData: {} as IProFormaReturnsData,
    waterfallAnalysis: {} as IWaterfallAnalysis,
    handleStartDevelopmentWithBOEEstimates: () => { },
    handleUpdateWaterfallAnalysis: () => { },
    handleUpdateDistributionWaterfall: () => { },
    handleUpdateProFormaPartners: () => { },
    handleUpdateLeasingCostReserves: () => { },
    handleUpdateRecoverableExpenses: () => { },
    handlePreviewLeasingCostReserves: () => { },
    periodCadence: Periods.MONTHLY,
    wattsType: DEFAULT_WATTS_TYPE,
    cashFlowPeriods: [], // to be replaced by `cashFlowPeriodsByCadence`
    cashFlowPeriodsByCadence: DEFAULT_BY_CADENCE,
    sourceTypes: [],
    showCents: false,
    handleToggleShowCents: () => { },
    updateLocalStorageByKey: () => { },
    getLocalStorageValueByKey: () => null,
    selectedForecastMetric: {} as IOptionAnyValue,
    setSelectedForecastMetric: () => { },
    projectActuals: undefined,
    projectActualsError: undefined,
    isLoadingProjectActuals: false,
    formatDisplayValue: formatProFormaValue,
    operationsCashFlow: [],
    updateProFormaLocal: () => { },
    isCalculating: false,
    roomMembers: [],
    canUndo: false,
    canRedo: false,
    handleUndo: () => { },
    handleRedo: () => { },
    isUndoLoading: false,
    isRedoLoading: false,
};

const ProFormaSocketContext = createContext<IProFormaSocketContext>(defaultValues);

interface IProFormaSocketProviderProps {
    organizationId: string;
    dealId: number;
    proFormaId: number;
    children: React.ReactNode;
}

const ProFormaSocketProvider = ({
    organizationId, dealId, proFormaId, children,
}: IProFormaSocketProviderProps) => {
    const roomId = `${organizationId}/deal/${dealId}/pro_forma/${proFormaId}`;
    const socketRef = useRef<any>(null);
    const leasingCostReservesCallbackRef = useRef<(() => void) | null>(null);
    const recoverableExpensesCallbackRef = useRef<(() => void) | null>(null);

    const isReconnectingRef = useRef(false);
    const { showError } = useToast();

    const [proForma, setProForma] = useState<IProFormaGeneralInfo>();
    const [cashFlowPeriods, setCashFlowPeriods] = useState<string[]>([]); // to be replaced by `cashFlowPeriodsByCadence`
    const [cashFlowPeriodsByCadence, setCashFlowPeriodsByCadence] = useState<Record<Periods, string[]>>(defaultValues.cashFlowPeriodsByCadence);
    const [uses, setUses] = useState<IUsesSummary>({} as IUsesSummary);
    const [sources, setSources] = useState<ISourcesSummary>({} as ISourcesSummary);
    const [buildableLots, setBuildableLots] = useState<IGrossBuildableAreaLotsData>(DEFAULT_EMPTY_LOT);
    const [isDefaultBudgetClassificationsLoading, setIsDefaultBudgetClassificationsLoading] = useState(false);
    const [isBudgetClassificationLoading, setIsBudgetClassificationLoading] = useState(false);
    const [isImportFromProjectBudgetClassificationLoading, setIsImportFromProjectBudgetClassificationLoading] = useState(false);
    const [isSourceDeletionLoading, setIsSourceDeletionLoading] = useState(false);
    const [isBudgetClassificationUpdatingLoading, setIsBudgetClassificationUpdatingLoading] = useState(false);
    const [isBudgetClassificationDeletionLoading, setIsBudgetClassificationDeletionLoading] = useState(false);
    const [isUpdatingSourcesMetricType, setIsUpdatingSourcesMetricType] = useState(false);
    const [isRecommendedCategoriesLoading, setIsRecommendedCategoriesLoading] = useState(false);
    const [isUndoLoading, setIsUndoLoading] = useState(false);
    const [isRedoLoading, setIsRedoLoading] = useState(false);

    // Delayed loading state - only shows after 1.5 seconds to avoid flicker on fast operations
    const {
        isLoading: isCalculating,
        startLoading: startCalculationLoading,
        stopLoading: stopCalculationLoading
    } = useDelayedLoading();

    const [operatingInfo, setOperatingInfo] = useState<IOperatingAndPermanentDebtInfo>({} as IOperatingAndPermanentDebtInfo);
    const [operatingPeriodCashFlows, setOperatingPeriodCashFlows] = useState<(IOperatingPeriodCashFlows | null)[]>([]);
    const [operationsCashFlowsByCadence, setOperationsCashFlowsByCadence] = useState<
        Record<Periods, IProFormaSocketRoomData['operatingPeriodCashFlows']>
    >(DEFAULT_BY_CADENCE);
    const [incomeSummary, setIncomeSummary] = useState<IIncomeSummary>({} as IIncomeSummary);
    const [expenses, setExpenses] = useState<IExpenseSummary>({} as IExpenseSummary);
    const [leasingCostReserves, setLeasingCostReserves] = useState<ILeasingCostReserves | undefined>(undefined);
    const [returnsData, setReturnsData] = useState<IProFormaReturnsData>({} as IProFormaReturnsData);
    const [disposition, setDisposition] = useState<IDispositionData | undefined>(undefined);
    const [waterfallAnalysis, setWaterfallAnalysis] = useState<IWaterfallAnalysis>({} as IWaterfallAnalysis);
    const [sourceTypes, setSourceTypes] = useState<ISourceType[]>([]);
    const [roomMembers, setRoomMembers] = useState<IEditor[]>([]);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    const [showCentsValue, setShowCentsValue] = useLocalStorage<boolean>(GlobalKeys.ShowCents, false);
    const [proFormaPageLocalStorage, setProFormaPageLocalStorage] = useLocalStorage<IProFormaPageLocalStorage>(PageSpecificKeys.ProFormaPage, {});
    const currentProFormaLocalStorage = proFormaId in proFormaPageLocalStorage ? proFormaPageLocalStorage[proFormaId] : {};
    const [shouldUseProjectActuals, setShouldUseProjectActuals] = useState(false);
    const [selectedForecastMetric, setSelectedForecastMetric] = useState<IOptionAnyValue>(FORECAST_METRIC_OPTIONS_NON_EARLY_PLANNING[0]);

    const { isAuthenticated, accessToken } = useAuth0TokenContext();

    const handleToggleShowCents = () => {
        setShowCentsValue(!showCentsValue);
        updateLocalStorageByKey(SHOW_CENTS_KEY, !showCentsValue);
    };

    const updateLocalStorageByKey = (key: string, value: any) => {
        if (key === SHOW_CENTS_KEY) {
            setShowCentsValue(value);
            return;
        }

        const updatedCurrentProForma = { ...currentProFormaLocalStorage, [key]: value };
        const updatedProFormaStorage = { ...proFormaPageLocalStorage, [proFormaId]: updatedCurrentProForma };
        setProFormaPageLocalStorage(updatedProFormaStorage as IProFormaPageLocalStorage);
    };

    const getLocalStorageValueByKey = (key: string) => {
        if (key === SHOW_CENTS_KEY) return showCentsValue;

        if (proFormaId in proFormaPageLocalStorage && key in proFormaPageLocalStorage[proFormaId]) {
            return proFormaPageLocalStorage[proFormaId][key as keyof typeof proFormaPageLocalStorage[typeof proFormaId]];
        }
        return null;
    };

    const checkSocketConnection = () => {
        if (!socketRef.current) {
            console.error('WebSocket connection is not established');
            return false;
        }
        return true;
    };

    const showErrorIfNotReconnecting = (message: string) => {
        if (!isReconnectingRef.current) showError(message);
    };

    /**
     * Wrapper for socket.emit that starts the delayed loading indicator.
     * Loading will automatically stop when setAllRoomData is called (on any broadcast).
     */
    const emitWithLoading = (event: string, data: any) => {
        startCalculationLoading();
        socketRef.current.emit(event, data);
    };

    const updateProFormaLocal = (patch: Partial<IProFormaGeneralInfo>) => {
        setProForma(current => ({ ...(current || ({} as IProFormaGeneralInfo)), ...patch }));
    };

    const localStorageScenarioId = getLocalStorageValueByKey(SELECTED_SCENARIO_KEY)
        ? Number(getLocalStorageValueByKey(SELECTED_SCENARIO_KEY)) : null;

    const localStoragePeriodCadence = getLocalStorageValueByKey(SELECTED_PERIOD_CADENCE_KEY)
        ? getLocalStorageValueByKey(SELECTED_PERIOD_CADENCE_KEY) as Periods : null;

    const localStorageWattsType = getLocalStorageValueByKey(WATTS_TYPE) as WattsType || DEFAULT_WATTS_TYPE;

    const { data: projectActuals, error: projectActualsError, isLoading: isLoadingProjectActuals } = useGetProFormaRoom(
        Number(dealId), proForma?.id ?? 0, organizationId, shouldUseProjectActuals, selectedForecastMetric.value, localStorageScenarioId,
        {
            enabled: shouldUseProjectActuals && Boolean(organizationId)
                && Boolean(proForma?.subprojectId) && proForma?.proFormaStatusId === PRO_FORMA_STATUS.PROJECT_CREATED,
        },
    );

    const setAllRoomData = (data: IProFormaSocketRoomData) => {
        // Stop any pending loading indicator when we receive updated data
        stopCalculationLoading();
        setIsUndoLoading(false);
        setIsRedoLoading(false);
        setProForma(data.generalInfo);
        setCashFlowPeriods(data.cashFlowPeriods || []); // to be replaced by `setCashFlowPeriodsByCadence`
        setCashFlowPeriodsByCadence({
            [Periods.MONTHLY]: data.cashFlowPeriods || [],
            [Periods.QUARTERLY]: data.cashFlowPeriods || [], // replace with quarterly cash flow periods when completed
            [Periods.ANNUALLY]: data.yearlyCashFlowPeriods || [],
        });
        setUses(data.usesSummary ?? ({} as IUsesSummary));
        setSources(data.sourcesSummary ?? ({} as ISourcesSummary));
        setBuildableLots(data.buildableLots);
        setOperatingInfo({ ...data.operatingInfo, ...data.permanentDebtInfo } as IOperatingAndPermanentDebtInfo);
        setOperatingPeriodCashFlows(data.operatingPeriodCashFlows || []); // to be replaced by `setOperationsCashFlowsByCadence`
        setOperationsCashFlowsByCadence({
            [Periods.MONTHLY]: data.operatingPeriodCashFlows || [],
            [Periods.QUARTERLY]: data.operatingPeriodCashFlows || [], // replace with quarterly cash flows when completed
            [Periods.ANNUALLY]: data.yearlyOperatingPeriodCashFlow || [],
        });
        setIncomeSummary(data.incomeSummary ?? ({} as IIncomeSummary));
        setExpenses(data.expenseSummary ?? ({} as IExpenseSummary));
        setLeasingCostReserves(data.expenseSummary?.leasingCostReserves ?? undefined);
        setDisposition(data.disposition ?? undefined);
        setShouldUseProjectActuals(shouldUseProjectActuals || false);
        setReturnsData(data.returns ?? ({} as IProFormaReturnsData));
        setWaterfallAnalysis(data.waterfallAnalysis ?? ({} as IWaterfallAnalysis));
        setSourceTypes(data.sourceTypes);
    };

    useEffect(() => {
        if (!isEmpty(projectActuals) && shouldUseProjectActuals) {
            console.log('VIEWING PROJECT ACTUALS', projectActuals);

            setAllRoomData(projectActuals);
        }
        if (!shouldUseProjectActuals && projectActuals && socketRef.current) {
            console.log('VIEWING PRO FORMA ROOM DATA');

            getProForma(socketRef.current, roomId)
                .then(setAllRoomData)
                .catch(console.error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectActuals, shouldUseProjectActuals]);

    useEffect(() => {
        // Reset all state when proFormaId changes
        setProForma(undefined);
        setCashFlowPeriods([]); // to be replaced by `setCashFlowPeriodsByCadence`
        setCashFlowPeriodsByCadence(defaultValues.cashFlowPeriodsByCadence);
        setUses({} as IUsesSummary);
        setSources({} as ISourcesSummary);
        setBuildableLots(DEFAULT_EMPTY_LOT);
        setOperatingInfo({} as IOperatingAndPermanentDebtInfo);
        setOperatingPeriodCashFlows([]); // to be replaced by `setOperationsCashFlowsByCadence`
        setOperationsCashFlowsByCadence(DEFAULT_BY_CADENCE);
        setIncomeSummary({} as IIncomeSummary);
        setExpenses({} as IExpenseSummary);
        setLeasingCostReserves(undefined);
        setDisposition(undefined);
        setReturnsData({} as IProFormaReturnsData);
        setWaterfallAnalysis({} as IWaterfallAnalysis);
        setSourceTypes([]);
        setRoomMembers([]);
        setCanUndo(false);
        setCanRedo(false);

        if (isAuthenticated && accessToken) {
            const socket = io(config.webSocketBaseUrl, {
                transports: ['websocket'],
                path: '/api/socket.io',
                auth: {
                    token: accessToken,
                },
                rejectUnauthorized: config.rejectUnauthorizedWebsocket,
                // Add reconnection settings to maintain connection
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                timeout: 20000,
            });

            socket.on('connect', async () => {
                console.log('Connected');
                console.log('Socket ID:', socket.id);
                console.log('Joining room:', roomId);
                isReconnectingRef.current = false;

                try {
                    console.log('Getting pro forma data: ', roomId);
                    await joinRoom(socket, roomId);
                    const data: IProFormaSocketRoomData = await getProForma(socket, roomId);
                    console.log('Pro forma data fetched:', data);
                    setAllRoomData(data);
                } catch (error) {
                    console.error('Error during connection workflow:', error);
                }
            });

            PRO_FORMA_WS_EVENT_NAMES.forEach(eventName => {
                socket.on(`pro_forma:${eventName}`, (data: IProFormaSocketRoomData) => {
                    console.log(`pro_forma:${eventName} event received:`, data);
                    setAllRoomData(data);

                    if (eventName === 'recoverableExpenses:updated' && recoverableExpensesCallbackRef.current) {
                        recoverableExpensesCallbackRef.current();
                        recoverableExpensesCallbackRef.current = null;
                    }

                    // Handle leasing cost reserves callback
                    if (eventName === 'leasingCostReserves:updated' && leasingCostReservesCallbackRef.current) {
                        console.log('Executing stored leasing cost reserves callback');
                        leasingCostReservesCallbackRef.current();
                        leasingCostReservesCallbackRef.current = null;
                    }
                });
            });

            socket.on('pro_forma:incomeTenants:created', (data: IProFormaSocketRoomData) => {
                console.log('Income tenant created:', data);
                setAllRoomData(data);
            });

            socket.on('pro_forma:incomeTenants:updated', (data: IProFormaSocketRoomData) => {
                console.log('Income tenant updated:', data);
                setAllRoomData(data);
            });

            socket.on('pro_forma:incomeTenants:deleted', (data: IProFormaSocketRoomData) => {
                console.log('Income tenant deleted:', data);
                setAllRoomData(data);
            });

            socket.on('room_presence', (data: { roomMembers: IEditor[] }) => {
                console.log('Room presence event received:', data);
                setRoomMembers(data.roomMembers);
            });

            socket.on('pro_forma:undo_redo_status', (data: { canUndo: boolean; canRedo: boolean }) => {
                setCanUndo(data.canUndo);
                setCanRedo(data.canRedo);
            });

            socket.on('connect_error', error => {
                console.error('WebSocket connection error:', error);
            });

            socket.on('disconnect', reason => {
                console.log('Disconnected:', reason);

                // io server and client disconnect specifically require manual reconnection and should show error toasts
                if (reason === 'io server disconnect') {
                    console.warn('Server forcefully disconnected the socket. No automatic reconnection will occur.');
                    isReconnectingRef.current = false;
                    showError('Connection closed by server. Please refresh the page.');
                    return;
                }

                if (reason === 'io client disconnect') {
                    console.log('Socket was manually disconnected. No automatic reconnection will occur.');
                    isReconnectingRef.current = false;
                    return;
                }

                // For transient disconnect ("transport close", "ping timeout", "transport error"), so auto reconnect so no error toasts
                isReconnectingRef.current = true;
            });

            socket.io.on('reconnect_attempt', attempt => {
                console.log('Reconnection attempt:', attempt);
                isReconnectingRef.current = true;
            });

            socket.io.on('reconnect', attemptNumber => {
                console.log('Reconnected after', attemptNumber, 'attempts');
                isReconnectingRef.current = false;
            });

            socketRef.current = socket;

            return () => {
                console.log('Cleaning up WebSocket connection');
                if (socket) {
                    if (roomId) {
                        // Leave the room before disconnecting to ensure proper cleanup
                        leaveRoom(socket, roomId)
                            .catch((error) => {
                                console.error('Error leaving room during cleanup:', error);
                            })
                            .finally(() => {
                                socket.disconnect();
                            });
                    }
                    else {
                        socket.disconnect();
                    }
                    socketRef.current = null;
                }
            };
        }

        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, accessToken, config.webSocketBaseUrl, proFormaId, roomId]);

    const handleUpdateProForma = (keyValues: IUpdateProFormaKeyValues[], successCallback?: () => void) => {
        console.log('Updating pro forma', keyValues);
        if (!checkSocketConnection()) return;

        const changedValues = keyValues.filter(({ key, value }) => {
            const currentValue = proForma?.[key as keyof IProFormaGeneralInfo];
            return currentValue !== value;
        });

        // If no values have changed, don't emit an update.
        if (changedValues.length === 0) {
            return;
        }

        const data = Object.fromEntries(
            changedValues.map(({ key, value }) => [key, value]),
        );

        emitWithLoading('update_pro_forma', {
            room: roomId,
            pro_forma_id: proFormaId,
            data,
        });

        socketRef.current.once('pro_forma:updated', (responseData: IProFormaSocketRoomData) => {
            setProForma(responseData.generalInfo);
            setUses(responseData.usesSummary ?? ({} as IUsesSummary));
            setSources(responseData.sourcesSummary ?? ({} as ISourcesSummary));

            if (successCallback) successCallback();
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error updating pro forma - please try again.');
        });
    };

    const handleCreateProFormaSource = (data: ICreateProFormaSourceRequestData, callback?: (isSuccess?: boolean) => void) => {
        console.log('Creating pro forma source', data);
        if (!checkSocketConnection()) return;

        emitWithLoading('create_pro_forma_source', {
            room: roomId,
            pro_forma_id: proFormaId,
            data,
        });

        socketRef.current.once('pro_forma:pro_forma_source:created', (responseData: IProFormaSocketRoomData) => {
            setUses(responseData.usesSummary ?? ({} as IUsesSummary));
            setSources(responseData.sourcesSummary ?? ({} as ISourcesSummary));

            if (callback) callback();
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error creating a source - please try again.');

            if (callback) callback(false);
        });
    };

    const handleCreateDefaultBudgetClassifications = (startUsesFromScratch: () => void) => {
        console.log('Creating default budget classifications');
        if (!checkSocketConnection()) return;

        setIsDefaultBudgetClassificationsLoading(true);
        socketRef.current.emit('create_default_budget_classifications', {
            room: roomId,
            pro_forma_id: proFormaId,
            organization_id: organizationId,
        });

        socketRef.current.once('pro_forma:default_budget_classifications:created', (data: IProFormaSocketRoomData) => {
            setIsDefaultBudgetClassificationsLoading(false);
            startUsesFromScratch();
        });

        socketRef.current.once('error', (error: any) => {
            console.error('WebSocket error:', error);
            setIsDefaultBudgetClassificationsLoading(false);
            showErrorIfNotReconnecting('Error creating default budget classifications - please try again.');
        });
    };

    const handleCreateRecommendedCategories = (successCallback: () => void) => {
        console.log('Creating recommended budget classifications');
        if (!checkSocketConnection()) return;

        setIsRecommendedCategoriesLoading(true);
        socketRef.current.emit('create_recommended_categories', {
            room: roomId,
            pro_forma_id: proFormaId,
            organization_id: organizationId,
        });

        socketRef.current.once('pro_forma:recommended_budget_classifications:created', (data: IProFormaSocketRoomData) => {
            setIsRecommendedCategoriesLoading(false);
            if (successCallback) successCallback();
        });

        socketRef.current.once('error', (error: any) => {
            console.error('WebSocket error:', error);
            setIsRecommendedCategoriesLoading(false);
            showErrorIfNotReconnecting('Error creating recommended categories - please try again.');
        });
    };

    const handleStartDevelopmentWithBOEEstimates = (scenarioId: string, startUsesFromScratch: () => void) => {
        console.log(`Creating budget classifications from boe scenario ${scenarioId}`);
        if (!checkSocketConnection()) return;

        setIsDefaultBudgetClassificationsLoading(true);
        socketRef.current.emit('start_development_with_boe_estimates', {
            room: roomId,
            pro_forma_id: proFormaId,
            organization_id: organizationId,
            scenario_id: scenarioId,
        });

        socketRef.current.once('pro_forma:start_development_with_boe_estimates:created', (data: IProFormaSocketRoomData) => {
            setIsDefaultBudgetClassificationsLoading(false);
            startUsesFromScratch();
        });

        socketRef.current.once('error', (error: any) => {
            console.error('WebSocket error:', error);
            setIsDefaultBudgetClassificationsLoading(false);
            showErrorIfNotReconnecting('Error starting development with BOE estimates - please try again.');
        });
    };

    const handleCreateBudgetClassification = (postBody: ICreateBudgetClassificationPostBody, expandCategory: () => void) => {
        console.log('Creating budget classification', postBody);
        if (!checkSocketConnection()) return;

        setIsBudgetClassificationLoading(true);
        emitWithLoading('create_budget_classification', {
            room: roomId,
            pro_forma_id: proFormaId,
            new_budget_classification: postBody,
        });

        socketRef.current.once('pro_forma:budget_classification:created', (data: IProFormaSocketRoomData) => {
            setIsBudgetClassificationLoading(false);
            expandCategory();
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            setIsBudgetClassificationLoading(false);
            showErrorIfNotReconnecting('Error creating budget classification - please try again.');
        });
    };

    const handleUpdateBudgetClassifications = (patchBody: IBudgetClassificationPatchData[], successCallback?: () => void) => {
        console.log('Updating budget classifications', patchBody);
        if (!checkSocketConnection()) return;

        setIsBudgetClassificationUpdatingLoading(true);
        emitWithLoading('update_budget_classifications', {
            room: roomId,
            pro_forma_id: proFormaId,
            update_budget_classifications: {
                budget_classifications: patchBody,
            },
        });

        socketRef.current.once('pro_forma:budget_classification:updated', (data: IProFormaSocketRoomData) => {
            setIsBudgetClassificationUpdatingLoading(false);
            if (successCallback) successCallback();
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            setIsBudgetClassificationUpdatingLoading(false);
            showErrorIfNotReconnecting('Error updating budget classifications - please try again.');
        });
    };

    const handleImportBudgetClassificationFromProject = (requestData: IImportBudgetClassificationFromProjectData, importUsesFromProject: () => void) => {
        console.log('Importing budget classification from project', requestData, roomId);
        if (!checkSocketConnection()) return;

        setIsImportFromProjectBudgetClassificationLoading(true);
        socketRef.current.emit('import_project_budget_classifications', {
            room: roomId,
            pro_forma_id: proFormaId,
            organization_id: organizationId,
            subproject_id: requestData.subprojectId,
            scenario_id: requestData.scenarioId,
            should_import_budget_lines: requestData.shouldImportBudgetLines,
            budget_multiplier_percentage: requestData.budgetMultiplierPercentage,
            should_copy_curve_fits: requestData.shouldImportCurveFits,
        });

        socketRef.current.once('pro_forma:budget_classifications_based_on_project:created', (data: IProFormaSocketRoomData) => {
            setIsImportFromProjectBudgetClassificationLoading(false);
            importUsesFromProject();
        });

        socketRef.current.once('error', (error: any) => {
            console.error('WebSocket error:', error);
            setIsImportFromProjectBudgetClassificationLoading(false);
            showErrorIfNotReconnecting('Error importing budget classification from project - please try again.');
        });
    };

    const handleDeleteBudgetClassification = (budgetClassificationIds: number[], deleteAll: boolean = false, successCallback: () => void) => {
        console.log(`Deleting ${deleteAll ?? 'all '}budget classification`, budgetClassificationIds);
        if (!checkSocketConnection()) return;

        setIsBudgetClassificationDeletionLoading(true);
        emitWithLoading('delete_budget_classification', {
            room: roomId,
            pro_forma_id: proFormaId,
            budget_classification_ids: budgetClassificationIds,
            delete_all: deleteAll,
        });

        socketRef.current.once('pro_forma:budget_classification:deleted', (data: IProFormaSocketRoomData) => {
            console.log('Budget classification was deleted:', data);
            setIsBudgetClassificationDeletionLoading(false);
            if (successCallback) successCallback();
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            setIsBudgetClassificationDeletionLoading(false);
            showErrorIfNotReconnecting('Error deleting budget classification - please try again.');
        });
    };

    const handleCopyBudgetCategorizations = (existingDealId: number, existingProFormaId: number, successCallback?: () => void) => {
        if (!checkSocketConnection()) return;

        setIsBudgetClassificationLoading(true);
        socketRef.current.emit('copy_budget_classifications_from_pro_forma', {
            room: roomId,
            pro_forma_id: proFormaId,
            organization_id: organizationId,
            existingDealId,
            existingProFormaId,
        });

        socketRef.current.once('pro_forma:budget_classifications:copied', (data: IProFormaSocketRoomData) => {
            console.log('Budget classifications were copied:', data);
            setIsBudgetClassificationLoading(false);
            if (successCallback) successCallback();
        });

        socketRef.current.once('error', (error: any) => {
            console.error('WebSocket error:', error);
            setIsBudgetClassificationLoading(false);
            showErrorIfNotReconnecting('Error copying budget classifications - please try again.');
        });
    };

    const handleUpdateSource = (proFormaSourceId: number, patchData: ISourcePatchData, successCallback?: () => void) => {
        if (!checkSocketConnection()) return;

        emitWithLoading('update_source', {
            room: roomId,
            pro_forma_id: proFormaId,
            pro_forma_source_id: proFormaSourceId,
            data: patchData,
        });

        socketRef.current.once('pro_forma:source:updated', () => {
            if (successCallback) successCallback();
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error updating source - please try again.');
        });
    };

    const handleUpdateSourcesMetricType = (proFormaId: number, metricType: DebtMetricType, successCallback?: () => void) => {
        console.log(`Updating sources metric type ${metricType} for pro forma ${proFormaId}`);
        if (!checkSocketConnection()) return;

        setIsUpdatingSourcesMetricType(true);
        socketRef.current.emit('update_source_metric_type', {
            room: roomId,
            pro_forma_id: proFormaId,
            metric_type: metricType,
            organization_id: organizationId,
        });

        socketRef.current.once('pro_forma:source:updated_metric_type', (data: IProFormaSocketRoomData) => {
            console.log('Sources were updated:', data);
            setIsUpdatingSourcesMetricType(false);
            if (successCallback) successCallback();
        });

        socketRef.current.once('error', (error: any) => {
            console.error('WebSocket error:', error);
            setIsUpdatingSourcesMetricType(false);
            showErrorIfNotReconnecting('Error updating sources - please try again.');
        });
    };

    const handledDeleteSource = (proFormaSourceId: number) => {
        console.log(`Deleting source ${proFormaSourceId}`);
        if (!checkSocketConnection()) return;

        setIsSourceDeletionLoading(true);
        socketRef.current.emit('delete_source', {
            room: roomId,
            pro_forma_source_id: proFormaSourceId,
        });

        socketRef.current.once('pro_forma:source:deleted', (data: IProFormaSocketRoomData) => {
            console.log('Source was deleted:', data);
            setIsSourceDeletionLoading(false);
        });

        socketRef.current.once('error', (error: any) => {
            console.error('WebSocket error:', error);
            setIsSourceDeletionLoading(false);
            showErrorIfNotReconnecting('Error deleting a source - please try again.');
        });
    };

    const handleUpdateProFormaOperations = (proFormaOperationsId: number, patchData: IPatchOperatingInfoData, skipUndoRedo = false) => {
        console.log('Updating pro forma operations', patchData);
        if (!checkSocketConnection()) return;

        emitWithLoading('update_pro_forma_operations', {
            room: roomId,
            pro_forma_id: proFormaId,
            pro_forma_operations_id: proFormaOperationsId,
            data: patchData,
            skipUndoRedo,
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error updating pro forma operations - please try again.');
        });
    };

    const handleCreateBuildableLot = () => {
        console.log('Creating gross buildable lot');
        if (!checkSocketConnection()) return;

        emitWithLoading('create_gross_buildable_lot', {
            room: roomId,
            pro_forma_id: proFormaId,
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error creating gross buildable lot - please try again.');
        });
    };

    const handleUpdateBuildableLot = (lotId: number, dataKey: keyof IGrossBuildableAreaLot, value: string | number) => {
        console.log('Updating gross buildable lot');
        if (!checkSocketConnection()) return;

        emitWithLoading('update_gross_buildable_lot', {
            room: roomId,
            pro_forma_id: proFormaId,
            lot_id: lotId,
            data: {
                [dataKey]: value,
            },
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error updating gross buildable lot - please try again.');
        });
    };

    const handleDeleteBuildableLot = (lotId: number) => {
        console.log('Deleting gross buildable lot');
        if (!checkSocketConnection()) return;

        emitWithLoading('delete_gross_buildable_lot', {
            room: roomId,
            pro_forma_id: proFormaId,
            lot_id: lotId,
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error deleting gross buildable lot - please try again.');
        });
    };

    const handleCreateOperationsRow = (handler: string, data?: Record<string, any>) => {
        if (!checkSocketConnection()) return;

        emitWithLoading(handler, {
            room: roomId,
            data: {
                name: DEFUALT_ROW_NAME_BY_HANDLER[handler],
                ...data,
            },
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error creating row - please try again.');
        });
    };

    const handleUpdateOperationsRow = (handler: string, rowId: number, dataKey: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        console.log(`Updating operations row with ID ${rowId} with data {${dataKey}: ${e.target.value}}`, handler);
        if (!checkSocketConnection()) return;

        emitWithLoading(handler, {
            room: roomId,
            data: {
                id: Number(rowId),
                [dataKey]: e.target.value,
            },
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error updating row - please try again.');
        });
    };

    const handleDeleteOperationsRow = (handler: string, rowId: number) => {
        console.log(`Deleting operations row with ID ${rowId}`, handler);
        if (!checkSocketConnection()) return;

        emitWithLoading(handler, {
            room: roomId,
            data: {
                id: Number(rowId),
            },
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error deleting row - please try again.');
        });
    };

    const handleUpdateProFormaAssumptions = (patchData: Partial<IAssumptions>) => {
        console.log('Updating disposition assumptions', patchData);
        if (!checkSocketConnection()) return;

        emitWithLoading('update_disposition_assumptions', {
            room: roomId,
            pro_forma_id: proFormaId,
            data: patchData,
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error updating pro forma dispositions assumptions - please try again.');
        });
    };

    const handleUpdateWaterfallAnalysis = (patchData: Partial<IWaterfallAnalysis>) => {
        console.log('Updating waterfall analysis details', patchData);
        if (!checkSocketConnection()) return;

        emitWithLoading('update_pro_forma_waterfall_analysis', {
            room: roomId,
            pro_forma_id: proFormaId,
            data: patchData,
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error updating waterfall analysis - please try again.');
        });
    };

    const handleUpdateDistributionWaterfall = (waterfallType: IWaterfallType, patchData: IUpdateDistributionWaterfallData) => {
        console.log(`Updating ${waterfallType} distribution waterfall`, patchData);
        if (!checkSocketConnection()) return;

        emitWithLoading('update_distribution_waterfall', {
            room: roomId,
            pro_forma_id: proFormaId,
            waterfall_type: waterfallType,
            data: patchData,
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error updating distribution waterfall - please try again.');
        });
    };

    const handleUpdateProFormaPartners = (patchData: IProFormaPartner) => {
        if (!checkSocketConnection()) return;

        emitWithLoading('update_pro_forma_partner', {
            room: roomId,
            pro_forma_id: proFormaId,
            data: patchData,
        });

        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error updating pro forma partners - please try again.');
        });
    };

    const handleUpdateRecoverableExpenses = (
        expenses: IRecoverableExpenseUpdate[],
        successCallback?: () => void,
    ) => {
        if (!checkSocketConnection()) return;

        recoverableExpensesCallbackRef.current = successCallback || null;
        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error updating recoverable expenses - please try again.');
            recoverableExpensesCallbackRef.current = null;
        });

        emitWithLoading('update_recoverable_expenses', {
            room: roomId,
            data: { expenses },
        });
    };

    const handleUpdateLeasingCostReserves = (data: ILeasingCostReserves, successCallback?: () => void) => {
        if (!checkSocketConnection()) return;

        leasingCostReservesCallbackRef.current = successCallback || null;
        socketRef.current.once('error', (error: any) => {
            stopCalculationLoading();
            console.error('WebSocket error:', error);
            showErrorIfNotReconnecting('Error updating leasing cost reserves - please try again.');
            leasingCostReservesCallbackRef.current = null;
        });

        emitWithLoading('update_leasing_cost_reserves', {
            room: roomId,
            data,
        });
    };

    const handlePreviewLeasingCostReserves = (data: ILeasingCostReserves, previewCallback?: (previewData: ILeasingCostReserves | null) => void) => {
        if (!checkSocketConnection()) return;

        socketRef.current.emit('preview_leasing_cost_reserves', {
            room: roomId,
            data,
        });

        if (previewCallback) {
            const handlePreviewSuccess = (previewData: ILeasingCostReserves) => {
                console.log('Leasing cost reserves preview received:', previewData);
                previewCallback(previewData);
            };

            const handlePreviewError = (error: any) => {
                console.error('WebSocket error during preview:', error);
                showErrorIfNotReconnecting('Error previewing leasing cost reserves - please try again.');
                previewCallback(null);
            };

            socketRef.current.once('leasingCostReserves:preview', handlePreviewSuccess);
            socketRef.current.once('error', handlePreviewError);
        }
    };

    const handleUndo = () => {
        if (!checkSocketConnection() || !canUndo) return;

        setIsUndoLoading(true);
        emitWithLoading('pro_forma_undo', {
            room: roomId,
        });
    };

    const handleRedo = () => {
        if (!checkSocketConnection() || !canRedo) return;

        setIsRedoLoading(true);
        emitWithLoading('pro_forma_redo', {
            room: roomId,
        });
    };

    const formatDisplayValue = (
        valueToFormat: any,
        showCents = showCentsValue,
        numberType?: 'currency' | 'percentage',
    ) => formatProFormaValue(valueToFormat, showCents, numberType);

    return (
        <ProFormaSocketContext.Provider
            value={{
                proForma: proForma || ({} as IProFormaGeneralInfo),
                handleUpdateProForma,
                handleCreateDefaultBudgetClassifications,
                isDefaultBudgetClassificationsLoading,
                handleCreateRecommendedCategories,
                isRecommendedCategoriesLoading,
                handleCreateBudgetClassification,
                isBudgetClassificationLoading,
                handleUpdateBudgetClassifications,
                isBudgetClassificationUpdatingLoading,
                handleDeleteBudgetClassification,
                isBudgetClassificationDeletionLoading,
                handleImportBudgetClassificationFromProject,
                isImportFromProjectBudgetClassificationLoading,
                handleCopyBudgetCategorizations,
                handledDeleteSource,
                handleUpdateSource,
                handleUpdateSourcesMetricType,
                isUpdatingSourcesMetricType,
                isSourceDeletionLoading,
                handleUpdateProFormaOperations,
                handleCreateProFormaSource,
                handleCreateBuildableLot,
                handleUpdateBuildableLot,
                handleDeleteBuildableLot,
                buildableLots,
                uses,
                sources,
                operatingInfo,
                operatingPeriodCashFlows,
                expenses,
                leasingCostReserves,
                incomeSummary,
                shouldUseProjectActuals,
                setShouldUseProjectActuals,
                handleCreateOperationsRow,
                handleUpdateOperationsRow,
                handleDeleteOperationsRow,
                socketRef,
                disposition,
                handleUpdateProFormaAssumptions,
                returnsData,
                waterfallAnalysis,
                handleStartDevelopmentWithBOEEstimates,
                handleUpdateWaterfallAnalysis,
                handleUpdateDistributionWaterfall,
                handleUpdateProFormaPartners,
                handleUpdateLeasingCostReserves,
                handleUpdateRecoverableExpenses,
                handlePreviewLeasingCostReserves,
                periodCadence: localStoragePeriodCadence || Periods.MONTHLY,
                wattsType: localStorageWattsType,
                cashFlowPeriods, // to be replaced by `cashFlowPeriodsByCadence`
                cashFlowPeriodsByCadence,
                sourceTypes,
                showCents: showCentsValue,
                handleToggleShowCents,
                updateLocalStorageByKey,
                getLocalStorageValueByKey,
                selectedForecastMetric,
                setSelectedForecastMetric,
                projectActuals,
                projectActualsError,
                isLoadingProjectActuals,
                formatDisplayValue,
                operationsCashFlow: operationsCashFlowsByCadence[localStoragePeriodCadence || Periods.MONTHLY] || [],
                roomId,
                updateProFormaLocal,
                isCalculating,
                roomMembers,
                canUndo,
                canRedo,
                handleUndo,
                handleRedo,
                isUndoLoading,
                isRedoLoading,
            }}
        >
            {children}
        </ProFormaSocketContext.Provider>
    );
};

const useProFormaSocketContext = () => useContext(ProFormaSocketContext);

export {
    ProFormaSocketContext, ProFormaSocketProvider, useProFormaSocketContext, defaultValues,
};

export interface IProFormaSocketContext {
    proForma: IProFormaGeneralInfo;
    roomId?: string;
    handleUpdateProForma: (keyValues: IUpdateProFormaKeyValues[], successCallback?: () => void) => void;
    handledDeleteSource: (proFormaSourceId: number) => void;
    isSourceDeletionLoading: boolean;
    handleCreateDefaultBudgetClassifications: (startUsesFromScratch: () => void) => void;
    isDefaultBudgetClassificationsLoading: boolean;
    handleCreateRecommendedCategories: (successCallback: () => void) => void;
    isRecommendedCategoriesLoading: boolean;
    handleCreateBudgetClassification: (postBody: ICreateBudgetClassificationPostBody, expandCategory: () => void) => void;
    isBudgetClassificationLoading: boolean;
    handleUpdateBudgetClassifications: (patchBody: IBudgetClassificationPatchData[], successCallback?: () => void) => void;
    isBudgetClassificationUpdatingLoading: boolean;
    handleDeleteBudgetClassification: (budgetClassificationIds: number[], deleteAll: boolean, successCallback: () => void) => void;
    isBudgetClassificationDeletionLoading: boolean;
    handleCopyBudgetCategorizations: (existingDealId: number, existingProFormaId: number, successCallback?: () => void) => void;
    handleCreateProFormaSource: (data: ICreateProFormaSourceRequestData, callback?: (isSuccess?: boolean) => void) => void;
    handleImportBudgetClassificationFromProject: (data: IImportBudgetClassificationFromProjectData, importUsesFromProject: () => void) => void;
    isImportFromProjectBudgetClassificationLoading: boolean;
    handleUpdateSource: (proFormaSourceId: number, patchData: ISourcePatchData, successCallback?: () => void) => void;
    handleUpdateSourcesMetricType: (proFormaId: number, metricType: DebtMetricType, successCallback?: () => void) => void;
    isUpdatingSourcesMetricType: boolean;
    uses: IUsesSummary;
    sources: ISourcesSummary;
    handleCreateBuildableLot?: () => void;
    handleUpdateBuildableLot?: (lotId: number, dataKey: keyof IGrossBuildableAreaLot, value: string | number) => void;
    handleDeleteBuildableLot?: (lotId: number) => void;
    handleUpdateProFormaOperations: (proFormaOperationsId: number, patchData: IPatchOperatingInfoData, skipUndoRedo?: boolean) => void;
    buildableLots: IGrossBuildableAreaLotsData;
    operatingInfo: IOperatingAndPermanentDebtInfo;
    operatingPeriodCashFlows: (IOperatingPeriodCashFlows | null)[];
    incomeSummary: IIncomeSummary;
    expenses: IExpenseSummary;
    leasingCostReserves: ILeasingCostReserves | undefined;
    shouldUseProjectActuals: boolean;
    setShouldUseProjectActuals: (value: boolean) => void;
    handleCreateOperationsRow: (handler: string, data?: Record<string, any>) => void;
    handleUpdateOperationsRow: (handler: string, rowId: number, dataKey: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleDeleteOperationsRow: (handler: string, rowId: number) => void;
    socketRef: React.MutableRefObject<any>;
    disposition?: IDispositionData;
    handleUpdateProFormaAssumptions: (patchData: Partial<IAssumptions>) => void;
    returnsData: IProFormaReturnsData;
    waterfallAnalysis: IWaterfallAnalysis;
    handleUpdateWaterfallAnalysis: (patchData: Partial<IWaterfallAnalysis>) => void;
    handleStartDevelopmentWithBOEEstimates: (scenarioId: string, startUsesFromScratch: () => void) => void;
    handleUpdateDistributionWaterfall: (
        waterfallType: IWaterfallType,
        patchData: IUpdateDistributionWaterfallData
    ) => void;
    handleUpdateProFormaPartners: (patchData: IProFormaPartner) => void;
    handleUpdateLeasingCostReserves: (data: ILeasingCostReserves, successCallback?: () => void) => void;
    handleUpdateRecoverableExpenses: (expenses: IRecoverableExpenseUpdate[], successCallback?: () => void) => void;
    handlePreviewLeasingCostReserves: (data: ILeasingCostReserves, previewCallback?: (previewData: ILeasingCostReserves | null) => void) => void;
    periodCadence: Periods;
    wattsType: WattsType;
    cashFlowPeriods: string[]; // to be replaced by `cashFlowPeriodsByCadence`
    cashFlowPeriodsByCadence: Record<Periods, string[]>;
    sourceTypes: ISourceType[];
    showCents: boolean;
    handleToggleShowCents: () => void;
    updateLocalStorageByKey: (key: string, value: any) => void;
    getLocalStorageValueByKey: (key: string) => string | null | any;
    selectedForecastMetric: IOptionAnyValue;
    setSelectedForecastMetric: (value: IOptionAnyValue) => void;
    projectActuals?: IProFormaSocketRoomData;
    projectActualsError?: any;
    isLoadingProjectActuals?: boolean;
    formatDisplayValue: (
        valueToFormat: any,
        showCents?: boolean,
        numberType?: 'currency' | 'percentage',
    ) => string;
    operationsCashFlow: IProFormaSocketRoomData['operatingPeriodCashFlows'];
    updateProFormaLocal: (patch: Partial<IProFormaGeneralInfo>) => void;
    isCalculating: boolean;
    roomMembers: IEditor[];
    canUndo: boolean;
    canRedo: boolean;
    handleUndo: () => void;
    handleRedo: () => void;
    isUndoLoading: boolean;
    isRedoLoading: boolean;
}
